import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { Link, createFileRoute, redirect } from '@tanstack/react-router';
import { CheckCircle2Icon } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';

import { parseAxiosError } from '@/api/api-axios';
import { useSignupMutation } from '@/api/api-hooks';
import { FormInput } from '@/components/Forms/FormInput';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { signupEnabled } from '@/config';

export const Route = createFileRoute('/_public/signup')({
  beforeLoad: () => {
    if (!signupEnabled) {
      throw redirect({ to: '/' });
    }
  },
  component: Signup,
});

// Matches ORG_DISPLAY_NAME_MAX_LENGTH on the backend's SignupRequest.
const WORKSPACE_NAME_MAX_LENGTH = 100;

type SignupFormValues = {
  display_name: string;
  email: string;
  captcha_token: string;
};

function Signup() {
  const { error, isError, isPending, isSuccess, mutateAsync: signup } = useSignupMutation();

  const form = useForm<SignupFormValues>({
    defaultValues: {
      display_name: '',
      email: '',
    },
    mode: 'onSubmit',
  });

  const { register, unregister, setValue } = form;
  const turnstileRef = useRef<TurnstileInstance | undefined>(undefined);

  // register manually
  useEffect(() => {
    register('captcha_token', {
      required: 'Please verify you are human',
    });

    return () => {
      unregister('captcha_token');
    };
  }, [register, unregister]);

  const onSubmit = async ({ captcha_token, display_name, email }: SignupFormValues) => {
    const workspaceName = display_name.trim();
    try {
      // A blank name is left out so the backend names the organization after the email.
      await signup(workspaceName ? { captcha_token, display_name: workspaceName, email } : { captcha_token, email });
    } catch {
      // Turnstile tokens are single-use, so retrying with the one just sent
      // would fail the bot check even after the real problem has cleared.
      // The mutation keeps the error for display; the form needs a fresh token.
      setValue('captcha_token', '');
      turnstileRef.current?.reset();
    }
  };

  const errorMessage = parseAxiosError(error, 'An error occurred while creating your account.');

  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-4 lg:p-8">
      <Card className="w-full max-w-4xl overflow-hidden shadow-lg shadow-black/5">
        <CardContent className="grid p-0 lg:grid-cols-2">
          <div className="relative flex flex-col justify-between bg-zinc-900 p-10 text-white">
            <div className="absolute inset-0 bg-[linear-gradient(to_right_bottom,rgba(100,100,250,0.2),rgba(0,0,0,0)),linear-gradient(transparent,rgba(0,0,0,0.5))] bg-cover bg-center" />
            <div className="relative z-20">
              <h2 className="mb-4 text-2xl font-bold">Start your journey</h2>
              <p className="text-zinc-300">Join the developers and teams building the future with Driver AI.</p>
            </div>
            <div className="flex items-center justify-center">
              <svg
                id="Layer_1"
                className="fill-white"
                xmlns="http://www.w3.org/2000/svg"
                width={260}
                height={260}
                viewBox="0 0 400 400"
              >
                <path d="M281.36,115.52c24.18,20.17,39.6,50.52,39.6,84.48,0,60.73-49.22,109.97-109.97,109.97h-61.67l132.05-194.45Z" />
                <path d="M210.99,90.03c26.66,0,51.1,9.49,70.12,25.28l-202.08,108.26V90.03h131.95Z" />
              </svg>
            </div>

            <div className="relative z-20 mt-12 lg:mt-auto">
              <blockquote className="space-y-2">
                <div className="mb-4 flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    // eslint-disable-next-line react/no-array-index-key -- TODO: decorative static list, order never changes
                    <svg key={i} className="h-5 w-5 fill-current text-yellow-400" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                    </svg>
                  ))}
                </div>
                <p className="text-lg leading-relaxed font-light">
                  &ldquo;What Claude Code is able to do with Driver is just so good. It&apos;s so good that I have to
                  turn it off to remind me what Claude Code is like with out it.&rdquo;
                </p>
              </blockquote>
            </div>
          </div>

          <div className="flex flex-col justify-center p-8 lg:p-12">
            <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
              {!isSuccess && (
                <div className="flex flex-col space-y-2 text-center">
                  <h1 className="text-2xl font-semibold tracking-tight">Create an account</h1>
                  <p className="text-muted-foreground text-sm">Enter your email below to create your account</p>
                </div>
              )}

              <Form {...form}>
                {isSuccess ? (
                  <div className="flex flex-col items-center gap-4 text-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                      <CheckCircle2Icon className="h-6 w-6 text-green-600" />
                    </div>
                    <h1 className="text-xl font-semibold">Check your email</h1>
                    <p className="text-muted-foreground">
                      We sent an invitation link to your email.
                      <br />
                      Open it to finish setting up your account.
                    </p>
                  </div>
                ) : (
                  <div className="grid gap-6">
                    <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
                      <FormField
                        control={form.control}
                        name="email"
                        rules={{ required: '* required' }}
                        render={({ field }) => (
                          <FormItem className="space-y-1">
                            <FormLabel className="sr-only">Work Email</FormLabel>
                            <FormControl>
                              <FormInput
                                id="work-email"
                                type="email"
                                autoComplete="email"
                                placeholder="name@example.com"
                                className="h-10"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="display_name"
                        render={({ field }) => (
                          <FormItem className="space-y-1">
                            <FormLabel className="sr-only">Company or workspace name (optional)</FormLabel>
                            <FormControl>
                              <FormInput
                                id="workspace-name"
                                type="text"
                                autoComplete="organization"
                                placeholder="Company or workspace name (optional)"
                                maxLength={WORKSPACE_NAME_MAX_LENGTH}
                                className="h-10"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <Turnstile
                        ref={turnstileRef}
                        siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                        options={{ responseFieldName: 'captcha_token' }}
                        onSuccess={(token) => {
                          setValue('captcha_token', token);
                        }}
                      />

                      <Button className="h-10 w-full" variant="driverBrandBlue" type="submit" disabled={isPending}>
                        {isPending && (
                          <svg
                            className="mr-2 h-4 w-4 animate-spin"
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                          </svg>
                        )}
                        Sign Up with Email
                      </Button>

                      {isError && <p className="text-destructive text-center text-sm">{errorMessage}</p>}
                    </form>
                  </div>
                )}
              </Form>

              {!isSuccess && (
                <p className="text-muted-foreground px-8 text-center text-sm">
                  By clicking continue, you agree to our{' '}
                  <a
                    href="https://www.driver.ai/terms-of-use"
                    className="hover:text-primary underline underline-offset-4"
                  >
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://www.driver.ai/privacy-policy"
                    className="hover:text-primary underline underline-offset-4"
                  >
                    Privacy Policy
                  </a>
                  .
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {!isSuccess && (
        <p className="text-muted-foreground mt-8 text-center text-sm">
          Already have an account?{' '}
          <Link to="/" className="text-primary font-medium hover:underline">
            Sign in
          </Link>
        </p>
      )}
    </div>
  );
}
